import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
  Area
} from "recharts";
import "../ai/chart.css";


function fmtDateYYYYMMDD(ymd) {
  const d = new Date(ymd + "T00:00:00Z");
  // mm/dd
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}
const toNum = (x) => (Number.isFinite(+x) ? +x : null);
const nfmt = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

export default function MetalsChart({
  stats,
  bars = [],                 // [{date,open,high,low,close,metal}] ascending
  smaLines = [],
  supports = [],
  resistances = []
}) {
  const series = useMemo(() => {
    if (!Array.isArray(bars) || bars.length === 0) return [];
    return bars.map((b) => ({
      time: b.date,
      open: toNum(b.open),
      high: toNum(b.high),
      low: toNum(b.low),
      close: toNum(b.close),
    }));
  }, [bars]);
  const [expanded, setExpanded] = useState(false);
  const frameStyle = expanded ? { height: "70vh" } : { height: 260 };


  if (!series.length) return <div className="chart-container">No chart data</div>;

  const CustomTooltip = ({ active, label, payload }) => {
    if (!active || !payload?.length) return null;
    // We plotted only "close", but payload[0].payload contains our whole row (open/high/low/close)
    const row = payload[0].payload;
    return (
      <div className="ms-tip">
        <div className="ms-tip-title">Date: {label}</div>
        <div className="ms-tip-row">
          <span className="ms-tip-k">Open</span>
          <span className="ms-tip-v ms-tip-open">{nfmt(row.open)}</span>
        </div>
        <div className="ms-tip-row">
          <span className="ms-tip-k">High</span>
          <span className="ms-tip-v ms-tip-high">{nfmt(row.high)}</span>
        </div>
        <div className="ms-tip-row">
          <span className="ms-tip-k">Low</span>
          <span className="ms-tip-v ms-tip-low">{nfmt(row.low)}</span>
        </div>
        <div className="ms-tip-row">
          <span className="ms-tip-k">Close</span>
          <span className="ms-tip-v ms-tip-close">{nfmt(row.close)}</span>
        </div>

        {smaLines?.length > 0 && (
          <>
            <div className="ms-tip-sep" />
            {smaLines.map((s) => (
              <div className="ms-tip-row" key={s.key}>
                <span className="ms-tip-k">{s.label}</span>
                <span className="ms-tip-v">{nfmt(s.val)}</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

return (
  <div className={`chart-container ${expanded ? "expanded" : ""}`}>
    <div className="chart-title">
      📈 {stats?.metal} Daily OHLC (last 360 Days)
      <button
        className="ms-expand-btn"
        onClick={() => setExpanded((v) => !v)}
        title={expanded ? "Close" : "Enlarge"}
      >
        {expanded ? "Close" : "Enlarge"}
      </button>
    </div>

    <div className="ms-chart-frame" style={frameStyle} onDoubleClick={() => setExpanded(v=>!v)}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series}>
          <CartesianGrid stroke="#2b2f3a" strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tickFormatter={fmtDateYYYYMMDD}
            minTickGap={20}
            stroke="#9aa0a6"
          />
          <YAxis
            orientation="right"
            width={70}
            tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
            domain={["auto", "auto"]}
            stroke="#9aa0a6"
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: "#2c7be5", strokeDasharray: "4 4", strokeWidth: 2, opacity: 0.8 }}
            wrapperStyle={{ outline: "none" }}
          />

          {/* Area under close for readability */}
          <Area
            type="monotone"
            dataKey="close"
            fill="#0b2447"
            fillOpacity={0.18}
            stroke="none"
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="close"
            stroke="#00bcd4"
            dot={false}
            activeDot={{ r: 3 }}
            strokeWidth={2}
          />

          {/* SMAs as horizontal refs */}
          {smaLines.map((sma) => (
            <ReferenceLine
              key={sma.key}
              y={sma.val}
              stroke={sma.color}
              strokeDasharray="4 4"
              label={{
                value: sma.label,
                position: "insideTopRight",
                style: { fill: "#cfe3ff", fontSize: 12 }
              }}
            />
          ))}

          {/* Support / Resistance (top 3 each) */}
          {(supports || []).slice(0, 3).map((y, i) => (
            <ReferenceLine
              key={`sup-${i}`}
              y={y}
              stroke="#2ecc71"
              strokeDasharray="2 3"
              label={{
                value: `S${i + 1} ${nfmt(y)}`,
                position: "insideTopLeft",
                style: { fill: "#9be7a3", fontSize: 11 }
              }}
            />
          ))}
          {(resistances || []).slice(0, 3).map((y, i) => (
            <ReferenceLine
              key={`res-${i}`}
              y={y}
              stroke="#e57373"
              strokeDasharray="2 3"
              label={{
                value: `R${i + 1} ${nfmt(y)}`,
                position: "insideTopLeft",
                style: { fill: "#ffcdd2", fontSize: 11 }
              }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Indicator quickline row */}
      <div className="ms-legend">
        <span className="ms-dot" style={{ background: "#00bcd4" }} /> Close
        <span className="ms-dot" style={{ borderColor: "#66bb6a" }} /> SMA20
        <span className="ms-dot" style={{ borderColor: "#29b6f6" }} /> SMA50
        <span className="ms-dot" style={{ borderColor: "#ef5350" }} /> SMA200
        <span className="ms-dot" style={{ borderColor: "#2ecc71" }} /> Support
        <span className="ms-dot" style={{ borderColor: "#e57373" }} /> Resistance
      </div>
    </div>
  </div>
  );
}
